import {
  memo, useState, useEffect, useMemo
} from 'react';
import DataGrid from 'react-data-grid'
import { 
  SearchField
} from '@aws-amplify/ui-react';
import { useForm, SubmitHandler } from 'react-hook-form'
import { groupBy as rowGrouper } from 'lodash';

import { 
  getRemotePicklistById,
  getMovementsById,
} from '../../states/PicklistAction'
import {
  DgtPdOrderPicklist,
  DgtPdOrderPicklistMovement,
} from '../../models';

import Comment from '../../components/Comment';
import HeaderDetail from '../../components/HeaderDetail'
import SpinnerLoading from '../../components/SpinnerLoading';

import {
  PicklistItemRecordTotalColumn,
  BasePicklistItemColumn
} from '../../constants/Column'
import { OrderPicklistMovement } from '../../components/types';

interface Inputs {
  productionOrder: string
}

export default memo(function PicklistDetail() {
  const [ movements, setMovements ] = useState<DgtPdOrderPicklistMovement[]>([])
  const [ picklist, setPicklist ] = useState<DgtPdOrderPicklist>()
  const [ comments, setComments ] = useState<DgtPdOrderPicklist[]>([])
  const [ picklistItems, setPicklistItems ] = useState<DgtPdOrderPicklist[]>([])
  const [ expandedGroupIds, setExpandedGroupIds ] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  );
  const [ loading, setLoading ] = useState<boolean>(false)

  const { 
    register, handleSubmit
  } = useForm<Inputs>()

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setLoading(true)
    const movements = await getMovementsById(data.productionOrder)
    setMovements(movements)

    const { header, comments, items } = await getRemotePicklistById(data.productionOrder)
    setComments(comments)
    setPicklist(header)
    setPicklistItems(items)
    setLoading(false)
  }

  useEffect(() => {
    if (movements.length > 0) {
      setExpandedGroupIds(new Set(movements
        .filter(mv => movements.filter(mv1 => mv1.materialCode === mv.materialCode).length > 1)
        .map(itm => itm.materialCode)
        ))
    }
  }, [ movements ])

  const rmPkgMovements = useMemo(() => {
    return movements.map(mv => {
      const barcodeItem = picklistItems.find(itm => itm.materialCode === mv.materialCode)?.barcodePkg
      if (barcodeItem) return { ...mv, barcode: barcodeItem }
      return mv
    }) as OrderPicklistMovement[]
  }, [ movements, picklistItems ])

  const itemSize = useMemo(() =>
    rmPkgMovements.length > 0 ? new Set(movements.map(itm => itm.materialCode)).size : picklistItems.length
  , [ movements, picklistItems, rmPkgMovements ])

  const reportColumns = useMemo(() => {
    const baseCols = Array.from(PicklistItemRecordTotalColumn)
    baseCols.push({
      key: 'vendorLotNo', name: 'Vendor Batch', width: 120,
      groupFormatter({ childRows }) {
        return <div className='font-bold'>{childRows.length === 1 ? childRows[0].vendorLotNo : '' }</div>
      }
    })
    return baseCols
  }, [])

  return <div className="page-container">
    <div className='flex flex-col items-center justify-center gap-3'>
      <h3 className='underline text-muted '>Order Picklist - ใบเบิก</h3>
      {/* Search box */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <SearchField
          label="search"
          placeholder="พิมพ์ PD Order"
          {...register('productionOrder')}
        />
      </form>
      <p className='italic text-muted'>ค้นหาใบเบิกด้วยเลข PD Order แล้วกดปุ่มแว่นขยาย</p>
    </div>
    {loading ? <SpinnerLoading /> :
    picklist && <>
      <HeaderDetail title={`Order : ${picklist?.pk}`} header={picklist} />
      <small>{itemSize} รายการ</small>
      {rmPkgMovements.length > 0 ? <DataGrid
        className='mb-3'
        style={{ height: "100%", contain: "none" }}
        columns={reportColumns}
        rows={rmPkgMovements}
        rowKeyGetter={(row) => row.id}
        groupBy={['materialCode']}
        rowGrouper={rowGrouper}
        expandedGroupIds={expandedGroupIds}
        onExpandedGroupIdsChange={setExpandedGroupIds}
        defaultColumnOptions={{ resizable: true }}
        rowHeight={(arg) => {
          if (arg.type === 'GROUP' && arg.row.childRows[0].barcode) return 55
          return 35
        }}
      /> : 
      <DataGrid
        className='mb-3'
        style={{ height: "100%", contain: "none" }}
        columns={BasePicklistItemColumn}
        rows={picklistItems}
        rowHeight={(arg) => {
          const itm = arg.row as DgtPdOrderPicklist
          if (itm.barcodePkg) return 55
          return 35
        }}
      />}
      <Comment comments={comments.sort((a, b) => a.sk.localeCompare(b.sk))} onPost={(c) => console.log(c)} readonly={true} />
    </>}
  </div>
})